/**
 * VoyageDetailsCard - Expandable card for voyage configuration
 */

import React from 'react';
import { MapPin, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

const VoyageDetailsCard = ({ 
  voyageDetails, 
  setVoyageDetails, 
  passagePlans,
  isExpanded,
  onToggle,
  isComplete
}) => {
  return (
    <div className={`rounded-lg border-2 transition-all ${
      isComplete ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
    }`}>
      <div 
        onClick={onToggle}
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin className={`h-5 w-5 ${isComplete ? 'text-green-600' : 'text-gray-600'}`} />
            <div>
              <h4 className="text-sm font-medium">Voyage Details</h4>
              <p className="text-xs text-gray-500">
                {voyageDetails.departurePort && voyageDetails.destinationPort ? 
                  `${voyageDetails.departurePort} → ${voyageDetails.destinationPort}` : 
                  'Configure voyage'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isComplete && <CheckCircle className="h-5 w-5 text-green-600" />}
            {isExpanded ? 
              <ChevronUp className="h-5 w-5 text-gray-400" /> : 
              <ChevronDown className="h-5 w-5 text-gray-400" />
            }
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="space-y-4 mt-4">
            {/* Passage Plan Selection */}
            {passagePlans && passagePlans.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Passage Plan
                </label>
                <select
                  value={voyageDetails.passagePlanId}
                  onChange={(e) => setVoyageDetails(prev => ({
                    ...prev,
                    passagePlanId: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select passage plan...</option>
                  {passagePlans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - {plan.from} to {plan.to}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Departure and Destination */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Departure Port
                </label>
                <input
                  type="text"
                  value={voyageDetails.departurePort}
                  onChange={(e) => setVoyageDetails(prev => ({
                    ...prev,
                    departurePort: e.target.value
                  }))}
                  placeholder="Enter departure port"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Destination Port
                </label>
                <input
                  type="text"
                  value={voyageDetails.destinationPort}
                  onChange={(e) => setVoyageDetails(prev => ({
                    ...prev,
                    destinationPort: e.target.value
                  }))}
                  placeholder="Enter destination"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>

            {/* Purpose and Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purpose
                </label>
                <select
                  value={voyageDetails.purpose}
                  onChange={(e) => setVoyageDetails(prev => ({
                    ...prev,
                    purpose: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="commercial">Commercial</option>
                  <option value="training">Training</option>
                  <option value="survey">Survey</option>
                  <option value="transfer">Transfer</option>
                  <option value="trials">Sea Trials</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Est. Duration
                </label>
                <input
                  type="text"
                  value={voyageDetails.estimatedDuration}
                  onChange={(e) => setVoyageDetails(prev => ({
                    ...prev,
                    estimatedDuration: e.target.value
                  }))}
                  placeholder="e.g., 4 hours"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={voyageDetails.notes}
                onChange={(e) => setVoyageDetails(prev => ({
                  ...prev,
                  notes: e.target.value
                }))}
                placeholder="Any additional voyage information..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoyageDetailsCard;